import React, { useEffect, useState } from 'react';
import { Box, Card, Typography, Breadcrumbs, Divider, Grid, Skeleton, useTheme, IconButton } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { InfoOutlined } from '@mui/icons-material';
import { getAssistantByIdStore } from 'store/assistants/thunks';
import { StatusChip } from 'commons/generics/StatusChip';
import { InfoModal } from 'components/generics/InfoModal'; 

export const DetailAsistentes = () => {
    const dispatch:any = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const theme = useTheme();
    
    const [item, setItem] = useState<any>(null);
    const [activeModal, setActiveModal] = useState<string | null>(null);

    const { form_options } = useSelector((state: any) => state.assistants);

    const getModalContent = () => {
        switch (activeModal) {
            case 'name': return { title: 'Nombre', description: 'Nombre del Asistente.', infoText: 'Nombre descriptivo.' };
            case 'description': return { title: 'Descripción', description: 'Descripción del Asistente.', infoText: 'Propósito del asistente.' };
            case 'service_id': return { title: 'Servicio IA', description: 'Modelo IA base.', infoText: 'Modelo IA que le da las capacidades al asistente.' };
            case 'systemPrompt': return { title: 'System Prompt', description: 'Instrucciones del asistente.', infoText: 'Define el comportamiento principal.' };
            case 'welcomeMessage': return { title: 'Mensaje de Bienvenida', description: 'Mensaje inicial del asistente.', infoText: 'El mensaje con el que el asistente saludará al usuario.' };
            case 'temperature': return { title: 'Temperatura', description: 'Nivel de creatividad.', infoText: 'Entre 0 (deterministico) y 2 (muy creativo).' };
            case 'topP': return { title: 'Top P', description: 'Muestreo de núcleo.', infoText: 'Entre 0 y 1.' };
            case 'maxTokens': return { title: 'Max Tokens', description: 'Máximo de tokens en respuesta.', infoText: 'Límite de salida.' };
            case 'isPublic': return { title: 'Público', description: 'Acceso público al asistente.', infoText: '' };
            case 'allowHistory': return { title: 'Historial', description: 'Permite recordar conversaciones.', infoText: '' };
            case 'allowImages': return { title: 'Imágenes', description: 'Procesar imágenes.', infoText: '' };
            case 'allowAudio': return { title: 'Audio', description: 'Procesar audio.', infoText: '' };
            case 'allowFileUpload': return { title: 'Subir Archivos', description: 'Procesar documentos adjuntos.', infoText: '' };
            case 'maxMessagesPerDay': return { title: 'Límite diario', description: 'Máximo de mensajes por día.', infoText: '' };
            case 'visibility': return { title: 'Visibilidad', description: 'Visibilidad del asistente.', infoText: 'Privado, Público o Interno.' };
            default: return { title: '', description: '', infoText: '' };
        }
    };
    const modalContent = getModalContent();

    const renderLabelWithInfo = (label: string, modalKey: string, color: string = '#696CFF') => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</Typography>
            <IconButton size="small" color="info" onClick={() => setActiveModal(modalKey)} sx={{ ml: 0.5, p: 0.5 }}>
                <InfoOutlined fontSize="small" />
            </IconButton>
        </Box>
    );

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        if (!id) return;
        const response = await dispatch(getAssistantByIdStore(id));
        if (response?.ok && response?.data) {
            setItem(response.data);
        } else {
            navigate('/manager/plataforma-ia/asistentes');
        }
    };


    if (!item) return null;

    const capabilities = [
        { key: 'isPublic', label: 'Es Público' },
        { key: 'allowHistory', label: 'Historial Habilitado' },
        { key: 'allowImages', label: 'Soporte Imágenes' },
        { key: 'allowAudio', label: 'Soporte Audio' },
        { key: 'allowFileUpload', label: 'Subida de Archivos' }
    ];

    const servicesOptions = item.services?.services || [];
    const serviceName = servicesOptions.find((s: any) => s.value === item.service_id)?.label || 'Servicio Desconocido';

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AutoAwesomeIcon sx={{ color: '#696CFF' }} />
                <Typography 
                    fontSize={28} 
                    sx={{ 
                        fontWeight: 800, 
                        background: 'linear-gradient(45deg, #696CFF 30%, #A8B8FF 90%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}
                >
                    Detalle del Asistente
                </Typography>
            </Box>

            <Breadcrumbs aria-label="breadcrumb" sx={{ paddingBottom: 2 }}>
                <Typography color="textPrimary" sx={{ fontWeight: 700 }}>Inteligencia Artificial</Typography>
                <Link color="inherit" to="/manager/plataforma-ia/asistentes" style={{ textDecoration: 'none', color: theme.palette.text.secondary }}>
                    Asistentes
                </Link>
                <Typography color="textPrimary">{item.name}</Typography>
            </Breadcrumbs>
            
            <Divider sx={{ marginBottom: 4 }}/>

            <Grid container spacing={3}>
                <Grid size={{xs: 12, md: 6}} >
                    <Card sx={{ 
                        p: 4, 
                        height: '100%',
                        borderRadius: 4, 
                        background: 'linear-gradient(135deg, rgba(105,108,255,0.03) 0%, rgba(255,255,255,1) 100%)',
                        boxShadow: '0px 10px 30px -10px rgba(105, 108, 255, 0.15)',
                        border: '1px solid rgba(105,108,255,0.1)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                            <Box sx={{ 
                                p: 1.5, 
                                borderRadius: '12px', 
                                backgroundColor: 'rgba(105,108,255,0.1)', 
                                display: 'flex', 
                                mr: 2,
                                boxShadow: '0 0 15px rgba(105,108,255,0.2)'
                            }}>
                                <SmartToyIcon sx={{ color: '#696CFF', fontSize: 28 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#333' }}>Información Básica</Typography>
                                <Typography variant="body2" color="text.secondary">Detalles del asistente</Typography>
                            </Box>
                        </Box>

                        <Grid container spacing={3}> 
                            <Grid size={{xs: 12, md: 6}}>
                                {renderLabelWithInfo('Servicio IA Base', 'service_id')}
                                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>{serviceName}</Typography>
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                {renderLabelWithInfo('Visibilidad', 'visibility')}
                                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                    {item.visibility || 'private'}
                                </Typography>
                            </Grid>
                            <Grid  size={{xs: 12}}>
                                {renderLabelWithInfo('Nombre', 'name')}
                                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.2rem', color: '#111' }}>{item.name}</Typography>
                            </Grid>
                            <Grid  size={{xs: 12}}>
                                {renderLabelWithInfo('Descripción', 'description')}
                                <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>{item.description || 'Sin descripción disponible.'}</Typography>
                            </Grid>
                            
                            <Grid  size={{xs: 12}}>
                                {renderLabelWithInfo('Mensaje de Bienvenida', 'welcomeMessage')}
                                <Box sx={{ mt: 0.5, p: 2, backgroundColor: '#f1f5f9', borderRadius: 2, fontFamily: 'monospace', color: '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {item.welcomeMessage || 'N/A'}
                                </Box>
                            </Grid>
                            <Grid  size={{xs: 12}}>
                                {renderLabelWithInfo('System Prompt', 'systemPrompt')}
                                <Box sx={{ mt: 0.5, p: 2, backgroundColor: '#f1f5f9', borderRadius: 2, fontFamily: 'monospace', color: '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {item.systemPrompt || 'N/A'}
                                </Box>
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>

                <Grid size={{xs: 12, md: 6}}>
                    <Grid container spacing={3}>
                        <Grid size={{xs: 12}}>
                            <Card sx={{ 
                        p: 4, 
                        height: '100%',
                        borderRadius: 4, 
                        background: 'linear-gradient(135deg, rgba(0,210,255,0.03) 0%, rgba(255,255,255,1) 100%)',
                        boxShadow: '0px 10px 30px -10px rgba(0, 210, 255, 0.15)',
                        border: '1px solid rgba(0,210,255,0.1)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                            <Box sx={{ 
                                p: 1.5, 
                                borderRadius: '12px', 
                                backgroundColor: 'rgba(0,210,255,0.1)', 
                                display: 'flex', 
                                mr: 2,
                                boxShadow: '0 0 15px rgba(0,210,255,0.2)'
                            }}>
                                <SettingsSuggestIcon sx={{ color: '#00D2FF', fontSize: 28 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#333' }}>Parámetros Avanzados</Typography>
                                <Typography variant="body2" color="text.secondary">Configuración del modelo subyacente</Typography>
                            </Box>
                        </Box>

                        <Grid container spacing={3}>
                            <Grid size={{xs: 12, md: 6}}>
                                {renderLabelWithInfo('Estado Actual', 'status', '#00b0d6')}
                                <Box sx={{ mt: 0.5 }}>
                                    <StatusChip status={item.status} />
                                </Box>
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                {renderLabelWithInfo('Temperatura', 'temperature', '#00b0d6')}
                                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>{item.temperature ?? 'N/A'}</Typography>
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                {renderLabelWithInfo('Top P', 'topP', '#00b0d6')}
                                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                    {item.topP ?? 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                {renderLabelWithInfo('Max Tokens', 'maxTokens', '#00b0d6')}
                                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                    {item.maxTokens ? new Intl.NumberFormat('en-US').format(item.maxTokens) + ' tkns' : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid size={{xs: 12, md: 6}}>
                                {renderLabelWithInfo('Max Msj / Día', 'maxMessagesPerDay', '#00b0d6')}
                                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                    {item.maxMessagesPerDay ?? 'N/A'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Card>
                        </Grid>

                        <Grid size={{xs: 12}}>
                            <Card sx={{ 
                        p: 4, 
                        borderRadius: 4, 
                        background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)',
                        boxShadow: '0px 10px 30px -10px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #e2e8f0'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Box sx={{ 
                                p: 1.5, 
                                borderRadius: '12px', 
                                backgroundColor: 'rgba(93,204,10,0.1)', 
                                display: 'flex', 
                                mr: 2,
                                boxShadow: '0 0 15px rgba(93,204,10,0.2)'
                            }}>
                                <BuildCircleIcon sx={{ color: '#5DCC0A', fontSize: 28 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Capacidades Habilitadas</Typography>
                                <Typography variant="body2" color="text.secondary">Funciones especiales activadas para este asistente</Typography>
                            </Box>
                        </Box>
                        
                        <Grid container spacing={2}>
                            {capabilities.map((cap) => {
                                const isSupported = item[cap.key];
                                return (
                                    <Grid size={{xs: 12, sm: 6}} key={cap.key}>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            p: 2, 
                                            borderRadius: 3, 
                                            backgroundColor: isSupported ? 'rgba(93, 204, 10, 0.05)' : '#f8fafc',
                                            border: isSupported ? '1px solid rgba(93, 204, 10, 0.2)' : '1px solid #f1f5f9',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                            }
                                        }}>
                                            {isSupported 
                                                ? <CheckCircleIcon sx={{ color: '#5DCC0A', mr: 1.5 }} />
                                                : <CancelIcon sx={{ color: '#cbd5e1', mr: 1.5 }} />
                                            }
                                            <Typography variant="body2" sx={{ 
                                                fontWeight: isSupported ? 700 : 500, 
                                                color: isSupported ? '#334155' : '#94a3b8' 
                                            }}>
                                                {cap.label}
                                            </Typography>
                                            <IconButton size="small" onClick={() => setActiveModal(cap.key)} sx={{ ml: 'auto' }}>
                                                <InfoOutlined fontSize="small" sx={{ color: isSupported ? '#5DCC0A' : '#94a3b8' }} />
                                            </IconButton>
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <InfoModal
                open={activeModal !== null}
                setOpen={(val: boolean) => { if (!val) setActiveModal(null); }}
                title={modalContent.title}
                description={modalContent.description}
                infoText={modalContent.infoText}
                icon="Info"
            />
        </Box>
    );
};
